import type { AuditableEntity } from "../types";

/**
 * Mirrors `backend/src/services/onboarding/entities/onboarding.ts`.
 * Keep the two in sync — the draft crosses the wire verbatim.
 */

export type OnboardingStatus =
  | "pending"
  | "in_progress"
  | "completed"
  | "skipped";

export type OnboardingAnalysisStatus = "idle" | "running" | "done" | "failed";

/**
 * Mirrors `backend/src/services/onboarding/services/company-proposal.ts`:
 * "Remplir depuis mon site" in the company settings. Only the fields the site
 * actually spoke about are present.
 */
export interface CompanyProposal {
  name?: string;
  about?: string;
  address?: string;
}

export interface CompanyProposalState {
  status: OnboardingAnalysisStatus;
  url: string;
  started_at: number | null;
  finished_at: number | null;
  error: string;
  proposal: CompanyProposal | null;
}

export type OnboardingInstallType =
  | "reuse_number"
  | "new_number"
  | "web_only"
  | "skip";

export interface OnboardingDraft {
  company?: {
    name?: string;
    about?: string;
    address?: string;
    website?: string;
    logo?: string;
    default_language?: string;
    default_country?: string;
    default_timezone?: string;
  };
  contact?: {
    mobile_phone?: string;
    /** Server-set, from the OTP token. Never write this from the client. */
    mobile_verified?: boolean;
  };
  agent?: {
    name?: string;
    default_greetings?: string;
    voice?: string;
    /** The avatar the client picked (e.g. "f03"); also decides `voice_gender`. */
    avatar_id?: string;
    voice_gender?: "male" | "female";
    background_audio?: string;
    /** Written by the LLM; the wizard shows them but does not edit them. */
    role?: string;
    identity?: string;
  };
  /** Opening hours as a `calendar_schedules.rrules` string. */
  schedule_rrules?: string;
  telephony?: {
    install_type?: OnboardingInstallType;
    existing_number?: string;
    notes?: string;
  };
  tools?: string[];
}

export interface OnboardingAnalysis {
  url: string;
  source: "fetch" | "browser" | "description";
  pages: string[];
  confidence: number;
  at: number;
  profile: Record<string, unknown>;
}

export interface Onboarding extends AuditableEntity {
  client_id: string;
  status: OnboardingStatus;
  current_step: string;
  website: string;
  description: string;
  analysis_status: OnboardingAnalysisStatus;
  analysis_started_at: number | null;
  analysis_error: string;
  draft: OnboardingDraft;
  analysis: OnboardingAnalysis | null;
  completed_at: number | null;
}

export interface OnboardingState {
  onboarding: Onboarding;
  /** The shared line to call to reach the agent, when the platform runs one. */
  demo_number: string | null;
}

export interface OnboardingResult {
  onboarding: Onboarding;
  demo_number: string | null;
  caller_number: string | null;
  number_request_id: string | null;
}

/** The agent's call back to the owner, once placed (`POST …/callback`). */
export interface CallbackRequest {
  session_id: string;
  /** How many more times the owner may ask. */
  remaining: number;
}

/**
 * Where that call stands: still ringing or on the line, over after somebody
 * answered, or over with nobody there (no answer, a voicemail).
 */
export type CallbackStatus = "calling" | "ended" | "missed";

/**
 * The wizard's steps, in order.
 *
 * The middle three exist to be answered *while the analysis runs*: none of them
 * depends on what the website says, so they cost the client nothing and buy the
 * inference the time it needs. `waiting` is the slack — only a client who
 * answers faster than we can read ever sees it.
 */
export const ONBOARDING_STEPS = [
  "website",
  "mobile",
  "telephony",
  "agent",
  "waiting",
  "company",
  "hours",
  "done",
] as const;

export type OnboardingStep = (typeof ONBOARDING_STEPS)[number];

/** The steps that get a segment in the progress bar. */
export const PROGRESS_STEPS: OnboardingStep[] = [
  "website",
  "mobile",
  "telephony",
  "agent",
  "company",
  "hours",
];
